import { createContext, type ReactNode, useContext, useState } from "react";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { InsertedItem } from "~/features/cart/cartV2/ProductEntryList";

interface LastCartItemContextType {
  alternateItems: InsertedItem[];
  getEntryIndex: (virtualIndex: number) => number;
  getInsertedItemAtIndex: (virtualIndex: number) => InsertedItem | undefined;
  handleInsert: (virtualIndex: number, code: string, designation: string) => void;
  handleClose: (virtualIndex: number) => void;
  recentlyAddedItem: ProductData | null;
  setRecentlyAddedItem: React.Dispatch<React.SetStateAction<ProductData | null>>;
  scrollIndex: number;
  setScrollIndex: React.Dispatch<React.SetStateAction<number>>;
  handleClear: () => void;
}

const LastCartItemContext = createContext<LastCartItemContextType | undefined>(undefined);

interface AlternateProductsProviderProps {
  children: ReactNode;
}

export const AlternateProductsProvider = ({ children }: AlternateProductsProviderProps) => {
  const [alternateItems, setAlternateItems] = useState<InsertedItem[]>([]);
  const [recentlyAddedItem, setRecentlyAddedItem] = useState<ProductData | null>(null);
  const [scrollIndex, setScrollIndex] = useState<number>(-1);

  // Translate virtual index to entry index considering inserted items
  const getEntryIndex = (virtualIndex: number) => {
    // Count how many inserted items are before virtualIndex
    const insertsBefore = alternateItems.filter((item) => item.index <= virtualIndex).length;
    return virtualIndex - insertsBefore;
  };

  // Check if virtual index corresponds to inserted item
  const getInsertedItemAtIndex = (virtualIndex: number) => alternateItems.find((item) => item.index === virtualIndex);

  const handleInsert = (virtualIndex: number, code: string, designation: string) => {
    const insertAt = virtualIndex;
    setAlternateItems((prev) => {
      // Insert new item and keep sorted by index
      const newItems = [...prev, { index: insertAt, code, designation }];
      newItems.sort((a, b) => a.index - b.index);
      return newItems;
    });
  };

  const handleClose = (virtualIndex: number) => {
    setAlternateItems((prev) => prev.filter((item) => item.index !== virtualIndex));
  };

  const handleClear = () => {
    setAlternateItems([]);
    setRecentlyAddedItem(null);
    setScrollIndex(-1);
  };

  return (
    <LastCartItemContext.Provider
      value={{
        alternateItems,
        recentlyAddedItem,
        scrollIndex,
        setScrollIndex,
        getEntryIndex,
        getInsertedItemAtIndex,
        handleInsert,
        handleClose,
        setRecentlyAddedItem,
        handleClear,
      }}
    >
      {children}
    </LastCartItemContext.Provider>
  );
};

export const useAlternateProducts = (): LastCartItemContextType => {
  const context = useContext(LastCartItemContext);
  if (!context) {
    throw new Error("useAlternateProducts must be used within a AlternateProductsProvider");
  }
  return context;
};
